/*
 * Purpose: Compose Register UI using stateless controls and CreatePassword.
 * Boundary: Layout only; no business logic; delegates state/actions to hook.
 * Invariants:
 * - Consumes object h from useAuthForm with state and handlers.
 * - Uses PhoneNumber component for cell input.
 * - ASCII only; no Unicode in comments or strings
 */
// Imports
import { useMemo, useState } from 'react';

import Chip from '../authform-chips';
import PhoneNumber from '../../../contact/phonenumber';
import CreatePassword from '../../../password/createpassword';
import ListofCheckboxes from '../../../selection/listofcheckboxes';
import Otp from "../../../contact/otp";

import { IdField, NameField, AgeField } from '../authform-controls';
import { PhoneContext } from '../../../contact/phonenumber';
import { PasswordContext } from '../../../password/createpassword';
import { CheckboxContext } from '../../../selection/listofcheckboxes';
import { OtpContext } from '../../../contact/otp';
import PrivacyPolicyModal from '../../../consent/privacypolicy';

// Component RegisterLayout:
// Stateless layout consuming hook output
// Receives hook output (state + handlers) from useAuthForm
export default function RegisterLayout(h) {
    const checkboxCtx = useMemo(() => ({ setCheckboxState: h.setAcceptedTCs }), [h.setAcceptedTCs]);
    const [policyOpen, setPolicyOpen] = useState(false);

    // Rendering - outer wrapper and page title
    return (
        <div className='space-y-3 md:space-y-6' aria-labelledby='register-title'>
            {/* Title - shows Register */}
            <h1 id='register-title' className='text-base md:text-xl font-semibold'>
                Register
            </h1>
            {/* Top status - chips row and hint text */}
            <div className='flex flex-col gap-1'>
                {/* Chip row - required field status + OTP indicator */}
                <div className='flex flex-wrap items-center gap-1.5 md:gap-2'>
                    <Chip ok={h.proceedRequired.id} warnWhenMissing>ID</Chip>
                    <Chip ok={h.proceedRequired.first} warnWhenMissing>First</Chip>
                    <Chip ok={h.proceedRequired.last} warnWhenMissing>Last</Chip>
                    <Chip ok={h.proceedRequired.cell} warnWhenMissing>Cell</Chip>
                    <Chip ok={h.proceedRequired.tcs} warnWhenMissing>TCs</Chip>
                    <Chip ok={h.proceedRequired.password} warnWhenMissing>Password</Chip>
                    {h.age !== null && h.age < 18 && <Chip ok={h.proceedRequired.guardian} warnWhenMissing>Legal Guardian</Chip>}
                    <Chip ok={h.otpState.isValid} muted={!h.otpState.hasRequested}>OTP for Register</Chip>
                </div>
                {/* Hint - below chips, shows missing list or ready message */}
                <p className={`mt-[4px] text-[12px] ${h.isProceedReady ? 'text-emerald-300' : 'text-orange-300'}`}>
                    {h.isProceedReady ? 'All required fields completed' : `Missing: ${h.missingList().join(', ')}`}
                </p>
            </div>
            {/* Main form container */}
            <div className='relative rounded-xl bg-neutral-950 ring-1 ring-neutral-800 p-2.5 md:p-5'>
                {/* Form - shared container */}
                <form onSubmit={h.requestRegistration} className='grid gap-3 md:gap-4' noValidate>
                    <div className='grid md:grid-cols-2 gap-4'>
                        {/* ID Number */}
                        <IdField
                            id='register-id'
                            value={h.form.idNumber}
                            onChange={(e) => h.setField('idNumber', e.target.value.replace(/\D/g, ''))}
                        />
                        {/* Age - derived from ID */}
                        <AgeField value={h.age} />
                    </div>
                    {/* Name fields - first and last */}
                    <div className='grid md:grid-cols-2 gap-4'>
                        <NameField
                            id='register-first'
                            label='First name'
                            value={h.form.firstName}
                            onChange={(e) => h.setField('firstName', e.target.value)}
                            autoComplete='given-name'
                        />
                        <NameField
                            id='register-last'
                            label='Last name'
                            value={h.form.lastName}
                            onChange={(e) => h.setField('lastName', e.target.value)}
                            autoComplete='family-name'
                        />
                    </div>
                    <PhoneContext.Provider value={useMemo(() =>
                        ({ setFullNumber: h.setCellNumber, setIsValid: h.setIsPhoneValid, setCountryCode: h.setCountryCode }),
                        [h.setCellNumber, h.setIsPhoneValid, h.setCountryCode])}>
                        <PhoneNumber
                            enableCountrySelector={true}
                            defaultCountry='ZA'
                            showValidationLabel={false}
                        />
                    </PhoneContext.Provider>
                    {h.internationalGateMessage && (
                        <p className='text-sm text-orange-300'>{h.internationalGateMessage}</p>
                    )}
                    {/* Minor: inline acknowledgment checkboxes */}
                    {h.isMinor && h.minorAcknowledgments.length > 0 && (
                        <div className='space-y-2'>
                            <label className='block text-sm text-neutral-300'>Registration Acknowledgements</label>
                            {h.minorAcknowledgments.map((ack, i) => (
                                <label key={i} className='flex items-start gap-3 p-3 md:p-4 border border-neutral-800 rounded-lg bg-neutral-900 cursor-pointer'>
                                    <input
                                        type='checkbox'
                                        checked={ack.checked}
                                        onChange={() => ack.onChange(!ack.checked)}
                                        className='mt-1 w-4 h-4 accent-emerald-500 flex-shrink-0'
                                    />
                                    <span className='text-sm text-neutral-300 leading-relaxed'>{ack.text}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    {/* Adult: ListofCheckboxes + separate tracking consent Yes/No */}
                    {!h.isMinor && (
                        <>
                            <CheckboxContext.Provider value={checkboxCtx}>
                                <ListofCheckboxes
                                    label='Registration Terms & Conditions'
                                    options={h.tcsToAccept}
                                    showValidationLabel={false}
                                />
                            </CheckboxContext.Provider>
                            {h.trackingConsentText && (
                                <div className='space-y-2'>
                                    <label className='block text-sm font-medium text-neutral-300'>Tracking Consent</label>
                                    <div className='p-3 md:p-4 border border-neutral-800 rounded-lg bg-neutral-900'>
                                        <p className='text-sm text-neutral-300 leading-relaxed'>{(() => {
                            const phrase = 'Read our Privacy Policy for full details';
                            const parts = h.trackingConsentText.split(phrase);
                            if (parts.length === 2) {
                                return <>{parts[0]}<button type='button' onClick={() => setPolicyOpen(true)} className='text-emerald-400 hover:text-emerald-300 underline cursor-pointer bg-transparent border-none p-0 font-inherit text-sm inline'>{phrase}</button>{parts[1]}</>;
                            }
                            return h.trackingConsentText;
                        })()}</p>
                                    </div>
                                    <div className='flex gap-3'>
                                        <button
                                            type='button'
                                            onClick={() => h.setTrackingConsentChoice(true)}
                                            className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition touch-manipulation ${
                                                h.trackingConsentChoice === true
                                                    ? 'bg-emerald-600 text-white ring-2 ring-emerald-400'
                                                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200'
                                            }`}
                                            aria-pressed={h.trackingConsentChoice === true}
                                        >
                                            Yes, I consent
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => h.setTrackingConsentChoice(false)}
                                            className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition touch-manipulation ${
                                                h.trackingConsentChoice === false
                                                    ? 'bg-orange-600 text-white ring-2 ring-orange-400'
                                                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200'
                                            }`}
                                            aria-pressed={h.trackingConsentChoice === false}
                                        >
                                            No, I decline
                                        </button>
                                    </div>
                                    <PrivacyPolicyModal open={policyOpen} onClose={() => setPolicyOpen(false)} />
                                </div>
                            )}
                        </>
                    )}
                    <hr className='my-0.5 md:my-1 border-neutral-800' />
                    <PasswordContext.Provider value={useMemo(() =>
                        ({ setPasswordState: h.setPasswordState }),
                        [h.setPasswordState])}>
                        <CreatePassword 
                            showChips={true}
                            showHeading={true} 
                            hideSecureMessage={true} 
                            auth={h.auth}
                         />
                    </PasswordContext.Provider>
                    <hr className='my-0.5 md:my-1 border-neutral-800' />
                    
                    {/* OTP Section */}
                    <OtpContext.Provider value={useMemo(() =>
                        ({ setOtpState: h.setOtpState }),
                        [h.setOtpState])}>
                        <Otp
                            auth={h.auth}
                            idNumber={h.form.idNumber}
                            cellNumber={h.cellNumber}
                            isProceedReadyOtp={h.isProceedReadyOtpRegister}
                            otpLength={6}
                            resetSignal={h.otpResetSignal}
                        />
                    </OtpContext.Provider>

                    {!h.isProceedReady && (
                        <>
                            <hr className='my-0.5 md:my-1 border-neutral-800' />
                            <p className='h-10 flex items-center justify-center rounded-lg bg-orange-900/20 text-sm text-orange-300'>
                                Registration requirements not met
                            </p>
                        </>
                    )}
                </form>

                {/* Transition overlay -- consistent with legal guardian pattern */}
                {h.showTransition && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-neutral-900/95 backdrop-blur-sm rounded-xl"
                         style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                        <div className="max-w-md mx-4 p-6 bg-neutral-800 rounded-xl ring-1 ring-emerald-600 shadow-2xl">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                                        {h.transitionMessage}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 h-1 bg-neutral-700 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full"
                                     style={{ animation: 'progress 2s ease-in-out forwards' }} />
                            </div>
                        </div>
                    </div>
                )}
                {/* Status message - polite live region */}
                {h.status && (
                    <p className='mt-4 text-sm text-neutral-400' role='status' aria-live='polite'>
                        {h.status}
                    </p>
                )}
            </div>
        </div>
    );
}